/**
 * Oil & Gas — ERP Integration Hub
 *
 * Surfaces ERP work orders captured during Response Planning (Step 8) and
 * provides an audit log of every outbound dispatch. Shows:
 *   • All dispatched incidents with ERP system + work order reference
 *   • Status (dispatched / closed / pending)
 *   • Closure linkage — SHA-256 sealed record reference
 *   • Dispatch audit trail table with full metadata
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plug, ListChecks, ExternalLink, RefreshCw,
  CheckCircle2, Clock, AlertOctagon, Lock,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ErpMission {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  metadata?: {
    vertical?: string;
    corridor_name?: string;
    step8_status?: string;
    step8_erp_system?: string;
    step8_work_order?: string;
    step8_team_lead?: string;
    step8_dispatched_at?: string;
    step8_eta_h?: number;
    step8_response_type?: string;
    closure_outcome?: string;
    closure_sha256?: string;
    closure_sealed_at?: string;
    [key: string]: any;
  };
}

// ─── ERP system badge colour ──────────────────────────────────────────────────

const ERP_COLOUR: Record<string, string> = {
  'SAP':         'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'SAP S/4HANA': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Oracle EBS':  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'Maximo':      'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

function erpBadge(sys?: string) {
  if (!sys) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return ERP_COLOUR[sys] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

const RESPONSE_LABELS: Record<string, string> = {
  ground_inspection: 'Ground Inspection',
  pressure_test:     'Pressure Test',
  shut_in:           'Shut-in Procedure',
  emergency_repair:  'Emergency Repair',
  leak_containment:  'Leak Containment',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const ErpIntegrationPage: React.FC = () => {
  const [missions, setMissions]   = useState<ErpMission[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [filterErp, setFilterErp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { mission_type: 'ogi_survey', limit: 200 } });
      const all: ErpMission[] = res.data?.missions ?? res.data?.data?.missions ?? [];
      // Only show missions that had an ERP work order recorded
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'oil_gas' &&
            m.metadata?.step8_work_order,
        ),
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load ERP records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Derive display status
  function displayStatus(m: ErpMission): 'closed' | 'dispatched' | 'pending' {
    if (m.metadata?.closure_outcome) return 'closed';
    if (m.metadata?.step8_status === 'dispatched') return 'dispatched';
    return 'pending';
  }

  const filtered = missions.filter((m) => {
    const sys = m.metadata?.step8_erp_system ?? '';
    const st  = displayStatus(m);
    if (filterErp    && sys !== filterErp)       return false;
    if (filterStatus && st !== filterStatus)     return false;
    return true;
  });

  // Stats
  const erpSystems = Array.from(new Set(missions.map((m) => m.metadata?.step8_erp_system).filter(Boolean))) as string[];
  const closedCount     = missions.filter((m) => displayStatus(m) === 'closed').length;
  const dispatchedCount = missions.filter((m) => displayStatus(m) === 'dispatched').length;
  const pendingCount    = missions.filter((m) => displayStatus(m) === 'pending').length;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Plug className="w-7 h-7 text-violet-500" /> ERP Integration Hub
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Outbound SAP / Oracle work order dispatch audit — all ERP references captured during Response Planning
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterErp} onChange={(e) => setFilterErp(e.target.value)} className={SELECT_CLS}>
            <option value="">All ERP Systems</option>
            {erpSystems.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={SELECT_CLS}>
            <option value="">All Statuses</option>
            <option value="dispatched">Dispatched</option>
            <option value="closed">Closed</option>
            <option value="pending">Pending</option>
          </select>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Stats ── */}
      {missions.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Work Orders',  value: missions.length,    colour: 'text-gray-900 dark:text-white' },
            { label: 'Dispatched',         value: dispatchedCount,    colour: 'text-amber-700 dark:text-amber-400' },
            { label: 'Closed & Sealed',    value: closedCount,        colour: 'text-green-700 dark:text-green-400' },
            { label: 'Pending',            value: pendingCount,       colour: 'text-blue-700 dark:text-blue-400' },
          ].map(({ label, value, colour }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── ERP system breakdown ── */}
      {erpSystems.length > 0 && (
        <Card padding="sm">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
            <ListChecks className="w-4 h-4 text-violet-500" /> Work Orders by ERP System
          </p>
          <div className="flex flex-wrap gap-2">
            {erpSystems.map((sys) => {
              const count = missions.filter((m) => m.metadata?.step8_erp_system === sys).length;
              return (
                <div key={sys} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800">
                  <Plug className="w-3.5 h-3.5 text-violet-500" />
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${erpBadge(sys)}`}>{sys}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{count} order{count !== 1 ? 's' : ''}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Dispatch audit table ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
            <ListChecks className="w-4 h-4" /> Dispatch Audit Trail ({filtered.length})
          </p>
          {(filterErp || filterStatus) && (
            <button
              onClick={() => { setFilterErp(''); setFilterStatus(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <Plug className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {missions.length === 0 ? 'No ERP work orders captured yet' : 'No records match the current filters'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {missions.length === 0
                ? 'Record a work order reference in Response Planning to see it here'
                : 'Try different filter values'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Incident', 'Corridor', 'ERP System', 'Work Order', 'Response Type', 'Team Lead', 'Dispatched', 'Status', 'Closure'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filtered.map((m) => {
                  const st = displayStatus(m);
                  return (
                    <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[180px] truncate">{m.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{m.metadata?.corridor_name ?? '—'}</td>
                      <td className="px-4 py-3">
                        {m.metadata?.step8_erp_system ? (
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${erpBadge(m.metadata.step8_erp_system)}`}>
                            {m.metadata.step8_erp_system}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-800 dark:text-gray-200 font-semibold">
                        {m.metadata?.step8_work_order ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                        {RESPONSE_LABELS[m.metadata?.step8_response_type ?? ''] ?? (m.metadata?.step8_response_type ?? '—')}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{m.metadata?.step8_team_lead ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {m.metadata?.step8_dispatched_at
                          ? new Date(m.metadata.step8_dispatched_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          st === 'closed'     ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          st === 'dispatched' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                          'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {st === 'closed'     && <CheckCircle2 className="w-3 h-3" />}
                          {st === 'dispatched' && <ExternalLink className="w-3 h-3" />}
                          {st === 'pending'    && <Clock className="w-3 h-3" />}
                          <span className="capitalize">{st}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-400 dark:text-gray-500 max-w-[140px] truncate">
                        {m.metadata?.closure_sha256 ? (
                          <span className="flex items-center gap-1">
                            <Lock className="w-3 h-3 flex-shrink-0" />
                            {m.metadata.closure_sha256.slice(0, 12)}…
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Connector notice ── */}
      <Card padding="sm">
        <div className="flex items-start gap-3">
          <AlertOctagon className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            <strong className="text-gray-700 dark:text-gray-300">Live ERP connector coming in MVP 3.5.</strong> Currently, work order IDs and ERP system references are recorded manually during Response Planning and stored as audit fields.
            The forthcoming connector will auto-generate SAP PM / Oracle EAM notifications, poll work order completion status, and surface a dead-letter queue for failed dispatches.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ErpIntegrationPage;

// Made with Bob
